/**
 * ExportPDFButton.tsx — thin UI trigger for PDF kosztorys export (PDF-01).
 *
 * BUNDLE SPLIT: pdfGenerator.tsx is dynamically imported at trigger time.
 * The Noto Sans font barrel (~1.5 MB) stays out of the main bundle.
 *
 * BUSINESS LOGIC RULE: Zero logic here — computes totals and delegates
 * to generateKosztorysPDF (in entities/estimate/lib). No rounding, no PDF,
 * no file handling in this component.
 *
 * I18N: All visible strings use t('ns.key', 'Fallback').
 * Keys referenced here (for Plan 03 to populate in 4 locales):
 *   pdf.export_button   → 'PDF'
 *   pdf.exporting       → 'Generuję...'
 *   pdf.export_error    → 'Błąd eksportu PDF'
 */

import React, { useState, useCallback } from 'react';
import { computeEstimateTotals } from '@/entities/estimate/lib/computeTotals';
import type { Room } from '@/entities/room/model/types';
import type { ProjectMeta, EstimateItem } from '@/entities/estimate/model/types';
import type { BOMTotalsResult } from '@/entities/material';
import i18n from '@/shared/config/i18n/i18n';
import { track } from '@/shared/lib/analytics';
import { useUIStore } from '@/stores/useUIStore';

const t = (key: string, fallback: string) => i18n.t(key, fallback) as string;

interface ExportPDFButtonProps {
  rooms: Room[];
  projectItems?: EstimateItem[];
  safetyMargin?: number;
  meta?: ProjectMeta;
  currency?: string;
  className?: string;
  /** CR-01 (MAT-04): optional material totals. When provided and tab is on, materials
   *  appear in the PDF. When absent → labour-only PDF (unchanged behaviour). */
  materialTotals?: BOMTotalsResult;
  /** TRUTH-02: optional project-level geometry override, forwarded to computeEstimateTotals
   *  so global items priced by project geometry match the screen. */
  projectOverrides?: { floorArea?: number; wallArea?: number; perimeter?: number };
}

export function ExportPDFButton({
  rooms,
  projectItems = [],
  safetyMargin = 0,
  meta = {},
  currency = 'PLN',
  className,
  materialTotals,
  projectOverrides,
}: ExportPDFButtonProps) {
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const vatRate = meta.vatRate ?? 0.23;
      // Compute totals using the shared authority (PDF-03 cardinal rule)
      // TRUTH-02: pass projectOverrides so global items priced by project geometry match the screen.
      const totals = computeEstimateTotals(rooms, projectItems, safetyMargin, vatRate, projectOverrides);

      // Dynamic import — keeps the font barrel (~1.5MB) out of the main bundle
      const { generateKosztorysPDF } = await import('@/entities/estimate/lib/pdfGenerator');
      // CR-01 (MAT-04): pass materialTotals when provided — renders materials section.
      // When absent → labour-only PDF (unchanged behaviour, byte-identical to pre-MAT-04).
      // TRUTH-03/05 (gap-closure): pass projectItems so global items render as rows too.
      await generateKosztorysPDF(totals, meta, rooms, vatRate, currency, materialTotals, projectItems);
      track('export', { format: 'pdf' });
      // ONBOARD-01: step 3 detection — set hasExported so the checklist knows this session exported
      useUIStore.getState().setHasExported(true);
    } catch (err) {
      console.error(t('pdf.export_error', 'Błąd eksportu PDF'), err);
    } finally {
      setExporting(false);
    }
  }, [rooms, projectItems, safetyMargin, meta, currency, materialTotals, projectOverrides, exporting]);

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={exporting}
      className={className}
      aria-busy={exporting}
    >
      {exporting ? t('pdf.exporting', 'Generuję...') : t('pdf.export_button', 'PDF')}
    </button>
  );
}
